import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import {
  Fetchexecutequery,
  Fetchupdatequery,
} from "../../../services/process/BRS1/brsService7";

import { headers, baseUrl } from "../../../services/common/credentials";
import { CreateData } from "../../../services/process/process_data/process_data";
import {
  Calendar, MapPin, ClipboardList, Users, BookOpen,
  ChevronLeft, ChevronRight, Check, Save, AlertCircle,
  CheckCircle, Zap, Plus, Edit3, Trash2, Eye,
  List, AlertTriangle, X, FileText, BarChart2,
  MoreVertical, Clock,
} from "lucide-react";

// ── Helpers ───────────────────────────────────────────────────────────────────
const getUserCredentials = () =>
  JSON.parse(sessionStorage.getItem("userCredentials") || "{}");

const getFormattedDate = () =>
  new Date().toISOString().slice(0, 19).replace("T", " ");

// ── Design tokens ─────────────────────────────────────────────────────────────
const T = {
  navy:        "#0a2352",
  navyDark:    "#061535",
  navyMid:     "#143272",
  orange:      "#f97316",
  orangeHover: "#ea6c0a",
  bg:          "#eef3f9",
  card:        "#ffffff",
  border:      "#d4dce8",
  text:        "#0f172a",
  muted:       "#64748b",
  light:       "#e8eef5",
  success:     "#16a34a",
  danger:      "#dc2626",
  warn:        "#d97706",
  purple:      "#7c3aed",
  accent:      "#2563eb",
};

// ── File upload config ────────────────────────────────────────────────────────
const STATIC_PATH   = "/home/demo/UserManagementModule/static/reference_files/";
const UPLOAD_URL    = `${baseUrl}/api/v2/user_modules/upload/`;
const DOWNLOAD_BASE = `${baseUrl}/api/v2/user_modules/serve-pdf/`;

// ── Option lists ──────────────────────────────────────────────────────────────
const EVENT_TYPE_OPTIONS = [
  "Education Fair", "Admission Open House", "Webinar", "Workshop",
  "Conference", "Faculty Exchange Program", "Student Recruitment Event",
  "Networking Session", "Other",
];

const EVENT_MODE_OPTIONS = ["In-Person", "Virtual", "Hybrid"];

const TARGET_AUDIENCE_OPTIONS = [
  "Undergraduate Students", "Graduate Students", "Parents",
  "School Counsellors", "Faculty Members", "Industry Professionals", "Other",
];

const PROGRAMS_OPTIONS = [
  "Engineering", "Business & Management", "Computer Science & AI",
  "Data Science", "Health Sciences", "Hospitality & Tourism",
  "Design & Architecture", "Arts & Humanities", "Other",
];

const YES_NO_OPTIONS = ["Yes", "No"];

// ── Step definitions ──────────────────────────────────────────────────────────
const STEPS = [
  {
    label: "Event Info",
    icon: ClipboardList,
    fields: ["event_title", "event_type", "event_mode", "event_date", "event_time", "venue", "description"],
  },
  {
    label: "Audience",
    icon: Users,
    fields: ["target_audience", "expected_participants"],
  },
  {
    label: "Academic Details",
    icon: BookOpen,
    fields: ["programs_featured", "scholarships_discussed", "rep_available"],
  },
];

const ALL_REQUIRED_FIELDS = STEPS.flatMap(s => s.fields);

// ── Status config ─────────────────────────────────────────────────────────────
const STATUS_META = {
  draft:      { label: "Draft",     bg: "#f3f4f6", color: "#6b7280" },
  published:  { label: "Published", bg: "#dbeafe", color: "#1d4ed8" },
  archived:   { label: "Archived",  bg: "#f3f4f6", color: "#6b7280" },
};

const statusBadge = (status) => {
  const m = STATUS_META[status] || { label: status, bg: "#f3f4f6", color: "#6b7280" };
  return (
    <span style={{
      background: m.bg, color: m.color,
      padding: "3px 12px", borderRadius: 99,
      fontSize: 13, fontWeight: 700, letterSpacing: .4,
      textTransform: "uppercase", whiteSpace: "nowrap",
    }}>
      {m.label}
    </span>
  );
};

const stepComplete = (stepIdx, fd) =>
  STEPS[stepIdx].fields.every(f => fd[f]);

const lastUnlockedStep = (fd) => {
  let last = 0;
  for (let i = 0; i < STEPS.length - 1; i++) {
    if (stepComplete(i, fd)) last = i + 1;
    else break;
  }
  return last;
};

const calcCompletion = (fd) => {
  const filled = ALL_REQUIRED_FIELDS.filter(f => fd[f]).length;
  return Math.round((filled / ALL_REQUIRED_FIELDS.length) * 100);
};

// ── Shared components ─────────────────────────────────────────────────────────
const Card = ({ children, style = {} }) => (
  <div style={{
    background: T.card, borderRadius: 14,
    border: `1px solid ${T.border}`,
    boxShadow: "0 4px 20px rgba(10,35,82,.07)",
    overflow: "hidden", ...style,
  }}>
    {children}
  </div>
);

const StatCard = ({ label, value, icon: Icon, color }) => (
  <Card style={{ padding: "18px 20px", flex: 1, minWidth: 140, overflow: "visible" }}>
    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
      <div>
        <div style={{ color: T.muted, fontSize: 14, fontWeight: 700, letterSpacing: .8, textTransform: "uppercase" }}>{label}</div>
        <div style={{ fontSize: 30, fontWeight: 900, color: T.text, lineHeight: 1.1, marginTop: 4 }}>{value}</div>
      </div>
      <div style={{ background: color + "18", borderRadius: 12, padding: 10 }}>
        <Icon size={22} color={color} />
      </div>
    </div>
  </Card>
);

const Field = ({ label, required, children, hint }) => (
  <div style={{ marginBottom: 18 }}>
    <label style={{
      display: "block", fontSize: 15, fontWeight: 700,
      color: T.navy, marginBottom: 6, letterSpacing: .3, textTransform: "uppercase",
    }}>
      {label}{required && <span style={{ color: T.danger, marginLeft: 3 }}>*</span>}
    </label>
    {children}
    {hint && <p style={{ fontSize: 13, color: T.muted, margin: "5px 0 0" }}>{hint}</p>}
  </div>
);

const inputBase = {
  width: "100%", padding: "10px 14px", borderRadius: 8,
  border: `1.5px solid ${T.border}`, fontSize: 16, color: T.text,
  background: "#fff", outline: "none", boxSizing: "border-box",
  transition: "border .18s, box-shadow .18s",
};

const Input = ({ style = {}, ...props }) => {
  const [focused, setFocused] = useState(false);
  return (
    <input {...props}
      style={{ ...inputBase, borderColor: focused ? T.orange : T.border, boxShadow: focused ? `0 0 0 3px rgba(249,115,22,.13)` : "none", ...style }}
      onFocus={() => setFocused(true)} onBlur={() => setFocused(false)}
    />
  );
};

const SelectInput = ({ children, style = {}, ...props }) => {
  const [focused, setFocused] = useState(false);
  return (
    <select {...props}
      style={{ ...inputBase, borderColor: focused ? T.orange : T.border, boxShadow: focused ? `0 0 0 3px rgba(249,115,22,.13)` : "none", ...style }}
      onFocus={() => setFocused(true)} onBlur={() => setFocused(false)}
    >
      {children}
    </select>
  );
};

const TextareaInput = ({ style = {}, ...props }) => {
  const [focused, setFocused] = useState(false);
  return (
    <textarea {...props}
      style={{ ...inputBase, borderColor: focused ? T.orange : T.border, boxShadow: focused ? `0 0 0 3px rgba(249,115,22,.13)` : "none", resize: "vertical", minHeight: 100, ...style }}
      onFocus={() => setFocused(true)} onBlur={() => setFocused(false)}
    />
  );
};

const Btn = ({ children, variant = "primary", size = "md", icon: Icon, onClick, disabled, style = {} }) => {
  const [hovered, setHovered] = useState(false);
  const pad = size === "sm" ? "7px 15px" : "11px 24px";
  const fs  = size === "sm" ? 15 : 17;
  const variants = {
    primary:   { background: hovered ? T.orangeHover : T.orange,  color: "#fff",  border: "none" },
    secondary: { background: hovered ? T.light : "#f1f5f9",        color: T.navy,  border: `1.5px solid ${T.border}` },
    outline:   { background: "transparent", color: hovered ? T.orange : T.navy, border: `1.5px solid ${hovered ? T.orange : T.border}` },
    success:   { background: hovered ? "#15803d" : T.success,      color: "#fff",  border: "none" },
    danger:    { background: hovered ? "#b91c1c" : T.danger,       color: "#fff",  border: "none" },
    navy:      { background: hovered ? T.navyMid : T.navy,         color: "#fff",  border: "none" },
  };
  return (
    <button onClick={onClick} disabled={disabled}
      onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)}
      style={{
        display: "inline-flex", alignItems: "center", gap: 6,
        borderRadius: 8, fontWeight: 700,
        cursor: disabled ? "not-allowed" : "pointer",
        padding: pad, fontSize: fs,
        transition: "all .15s", opacity: disabled ? .6 : 1,
        ...variants[variant], ...style,
      }}
    >
      {Icon && <Icon size={size === "sm" ? 14 : 16} />}
      {children}
    </button>
  );
};

const InfoBox = ({ children }) => (
  <div style={{
    display: "flex", alignItems: "flex-start", gap: 10, marginBottom: 20,
    padding: "12px 16px", background: "rgba(10,35,82,.05)",
    borderRadius: 8, border: `1px solid ${T.border}`,
  }}>
    <AlertCircle size={16} color={T.navy} style={{ flexShrink: 0, marginTop: 2 }} />
    <span style={{ fontSize: 16, color: T.muted, lineHeight: 1.6 }}>{children}</span>
  </div>
);

// ── Multi-select pill + optional "Other" text input ───────────────────────────
const PillGroup = ({ options, value, onChange, otherValue, onOtherChange }) => {
  const selected = value ? value.split(",").map(s => s.trim()).filter(Boolean) : [];
  const toggle = (opt) => {
    const next = selected.includes(opt)
      ? selected.filter(o => o !== opt)
      : [...selected, opt];
    if (!next.includes("Other") && onOtherChange) onOtherChange("");
    onChange(next.join(","));
  };
  return (
    <div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
        {options.map(opt => {
          const sel = selected.includes(opt);
          return (
            <button key={opt} type="button" onClick={() => toggle(opt)}
              style={{
                padding: "7px 15px", borderRadius: 99, fontSize: 15, fontWeight: 700,
                cursor: "pointer", transition: "all .15s",
                border: `1.5px solid ${sel ? T.orange : T.border}`,
                background: sel ? "rgba(249,115,22,.12)" : "#fff",
                color: sel ? T.orange : T.muted,
              }}
            >
              {opt}
            </button>
          );
        })}
      </div>
      {selected.includes("Other") && onOtherChange && (
        <Input
          value={otherValue || ""}
          onChange={e => onOtherChange(e.target.value)}
          placeholder="Please specify…"
          style={{ marginTop: 10, maxWidth: 420 }}
        />
      )}
    </div>
  );
};

// ── Radio pill group (Yes / No) ───────────────────────────────────────────────
const RadioGroup = ({ options, value, onChange }) => (
  <div style={{ display: "flex", gap: 10 }}>
    {options.map(opt => {
      const sel = value === opt;
      return (
        <button key={opt} type="button" onClick={() => onChange(opt)}
          style={{
            padding: "8px 28px", borderRadius: 99, fontSize: 15, fontWeight: 700,
            cursor: "pointer", transition: "all .15s",
            border: `1.5px solid ${sel ? T.orange : T.border}`,
            background: sel ? "rgba(249,115,22,.12)" : "#fff",
            color: sel ? T.orange : T.muted,
          }}
        >
          {opt}
        </button>
      );
    })}
  </div>
);

// ── Confirm delete dialog ──────────────────────────────────────────────────────
const ConfirmDialog = ({ message, onConfirm, onCancel }) => (
  <div style={{ position: "fixed", inset: 0, background: "rgba(6,21,53,.55)", zIndex: 999, display: "flex", alignItems: "center", justifyContent: "center" }}>
    <div style={{ background: "#fff", borderRadius: 14, padding: 28, maxWidth: 420, width: "90%", boxShadow: "0 20px 60px rgba(6,21,53,.25)" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
        <AlertTriangle size={22} color={T.danger} />
        <span style={{ fontWeight: 800, fontSize: 16, color: T.text }}>Confirm Delete</span>
      </div>
      <p style={{ fontSize: 14, color: T.muted, margin: "0 0 20px", lineHeight: 1.6 }}>{message}</p>
      <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
        <Btn variant="outline" onClick={onCancel}>Cancel</Btn>
        <Btn variant="danger" icon={Trash2} onClick={onConfirm}>Delete</Btn>
      </div>
    </div>
  </div>
);

// ── View modal ────────────────────────────────────────────────────────────────
const ViewModal = ({ record, onClose }) => {
  if (!record) return null;
  const rows = [
    ["Event Title",          record.event_title],
    ["Event Type",           record.event_type === "Other" ? record.custom_event_type || "Other" : record.event_type],
    ["Event Mode",           record.event_mode],
    ["Event Date",           record.event_date],
    ["Event Time",           record.event_time],
    ["Venue / Platform",     record.venue],
    ["Target Audience",      record.target_audience],
    ["Other Audience",       record.custom_target_audience],
    ["Expected Participants",record.expected_participants],
    ["Programs Featured",    record.programs_featured],
    ["Other Programs",       record.custom_programs],
    ["Scholarships Discussed",record.scholarships_discussed],
    ["Rep Available 1-on-1", record.rep_available],
    ["Status",               record.event_status],
  ];
  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(6,21,53,.55)", zIndex: 999, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
      <div style={{ background: "#fff", borderRadius: 14, width: "100%", maxWidth: 660, maxHeight: "90vh", overflow: "auto", boxShadow: "0 20px 60px rgba(6,21,53,.25)" }}>
        <div style={{ padding: "16px 24px", background: `linear-gradient(135deg, ${T.navy} 0%, ${T.navyMid} 100%)`, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <span style={{ color: "#fff", fontWeight: 800, fontSize: 16 }}>Event Details</span>
          <button onClick={onClose} style={{ background: "none", border: "none", color: "#fff", cursor: "pointer" }}><X size={18} /></button>
        </div>
        <div style={{ padding: 24 }}>
          {record.description && (
            <div style={{ marginBottom: 18, padding: "12px 16px", background: T.bg, borderRadius: 10, fontSize: 14, color: T.text, lineHeight: 1.7 }}>
              <div style={{ fontWeight: 800, color: T.navy, textTransform: "uppercase", fontSize: 12, letterSpacing: .3, marginBottom: 6 }}>Description</div>
              {record.description}
            </div>
          )}
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 14 }}>
            <tbody>
              {rows.map(([label, value]) => value ? (
                <tr key={label} style={{ borderBottom: `1px solid ${T.border}` }}>
                  <td style={{ padding: "10px 12px", fontWeight: 700, color: T.navy, width: "42%", textTransform: "uppercase", fontSize: 12, letterSpacing: .3 }}>{label}</td>
                  <td style={{ padding: "10px 12px", color: T.text }}>{value}</td>
                </tr>
              ) : null)}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

// ═════════════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// =============================================================================
export function UniversityEventManagement() {
  const uc = getUserCredentials();
  const editIdRef = useRef(null);

  const [view, setView]                 = useState("list");
  const [records, setRecords]           = useState([]);
  const [listLoading, setListLoading]   = useState(true);
  const [searchText, setSearchText]     = useState("");
  const [viewRecord, setViewRecord]     = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const [step, setStep]             = useState(0);
  const [saving, setSaving]         = useState(false);
  const [saveStatus, setSaveStatus] = useState(null);
  const [stepError, setStepError]   = useState("");

  // ── Empty form ──────────────────────────────────────────────────────────
  const emptyForm = {
    // Step 0 — Event Info
    event_title:          "",
    event_type:           "",
    custom_event_type:    "",
    event_mode:           "",
    event_date:           "",
    event_time:           "",
    venue:                "",
    description:          "",

    // Step 1 — Audience
    target_audience:         "",
    custom_target_audience:  "",
    expected_participants:   "",

    // Step 2 — Academic Details
    programs_featured:    "",
    custom_programs:      "",
    scholarships_discussed: "",
    rep_available:        "",

    event_status: "draft",
  };

  const [formData, setFormData] = useState({ ...emptyForm });

  // ── Fetch list ────────────────────────────────────────────────────────
  const fetchRecords = async () => {
    setListLoading(true);
    try {
      const creds = Fetchexecutequery();
      const res = await axios.post(creds[1], {
        query: `SELECT primary_id, event_title, event_type, custom_event_type,
                       event_mode, event_date, event_time, venue,
                       target_audience, expected_participants,
                       scholarships_discussed, rep_available,
                       event_status, created_date, modified_date
                FROM edu_platform.university_events
                WHERE is_active = 1
                  AND creator_entity_id = ${uc.selectedentity || uc.entities_id}
                  AND tenant_id = ${uc.tenants_id}
                ORDER BY primary_id DESC;`,
      }, creds[0]);
      setRecords(res.data || []);
    } catch (err) { console.error("Fetch error:", err); }
    finally { setListLoading(false); }
  };

  useEffect(() => { fetchRecords(); }, []);

  const totalCount     = records.length;
  const publishedCount = records.filter(r => r.event_status === "published").length;
  const draftCount     = records.filter(r => r.event_status === "draft").length;
  const virtualCount   = records.filter(r => r.event_mode === "Virtual").length;

  // ── AG Grid columns ───────────────────────────────────────────────────
  const columnDefs = [
    {
      headerName: "Event Title", field: "event_title", flex: 2, minWidth: 200,
      cellRenderer: p => <span style={{ fontWeight: 700, color: T.navy, fontSize: 14 }}>{p.value}</span>,
    },
    {
      headerName: "Type", field: "event_type", width: 160,
      cellRenderer: p => p.value === "Other" ? (p.data.custom_event_type || "Other") : (p.value || "—"),
    },
    { headerName: "Mode",  field: "event_mode",  width: 110 },
    {
      headerName: "Date",  field: "event_date",  width: 120,
      cellRenderer: p => p.value ? new Date(p.value).toLocaleDateString("en-GB") : "—",
    },
    { headerName: "Time",  field: "event_time",  width: 100 },
    { headerName: "Venue", field: "venue",       flex: 1, minWidth: 140 },
    {
      headerName: "Status", field: "event_status", width: 140,
      cellRenderer: p => statusBadge(p.value),
    },
    {
      headerName: "Actions", width: 140, sortable: false, filter: false,
      cellRenderer: p => (
        <div style={{ display: "flex", gap: 6, alignItems: "center", height: "100%" }}>
          <button title="View" onClick={() => setViewRecord(p.data)}
            style={{ background: "rgba(10,35,82,.08)", border: "none", borderRadius: 6, padding: "5px 9px", cursor: "pointer", color: T.navy, display: "flex", alignItems: "center" }}>
            <Eye size={14} />
          </button>
          <button title="Edit" onClick={() => openEdit(p.data.primary_id)}
            style={{ background: "rgba(249,115,22,.1)", border: "none", borderRadius: 6, padding: "5px 9px", cursor: "pointer", color: T.orange, display: "flex", alignItems: "center" }}>
            <Edit3 size={14} />
          </button>
          <button title="Delete" onClick={() => setDeleteTarget({ primary_id: p.data.primary_id, event_title: p.data.event_title })}
            style={{ background: "rgba(220,38,38,.1)", border: "none", borderRadius: 6, padding: "5px 9px", cursor: "pointer", color: T.danger, display: "flex", alignItems: "center" }}>
            <Trash2 size={14} />
          </button>
        </div>
      ),
    },
  ];

  // ── Open edit ─────────────────────────────────────────────────────────
  const openEdit = async (primaryId) => {
    try {
      const creds = Fetchexecutequery();
      const res = await axios.post(creds[1], {
        query: `SELECT * FROM edu_platform.university_events WHERE primary_id = ${primaryId} AND is_active = 1 LIMIT 1;`,
      }, creds[0]);
      if (res.data && res.data.length > 0) {
        const row = res.data[0];
        const fd = {};
        Object.keys(emptyForm).forEach(k => { fd[k] = row[k] ?? ""; });
        setFormData(fd);
        setStep(0);
        setStepError("");
        setSaveStatus(null);
        editIdRef.current = primaryId;
        setView("form");
      }
    } catch (err) { console.error("Load error:", err); }
  };

  const openCreate = () => {
    setFormData({ ...emptyForm });
    setStep(0);
    setStepError("");
    setSaveStatus(null);
    editIdRef.current = null;
    setView("form");
  };

  // ── Delete ─────────────────────────────────────────────────────────────
  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      const creds = Fetchupdatequery();
      await axios.post(creds[1], {
        schema_name: "edu_platform",
        table: "university_events",
        set_column: [{ is_active: 0, modified_date: getFormattedDate(), modified_by: uc.user_id }],
        where: [{ primary_id: deleteTarget.primary_id }],
      }, creds[0]);
      setDeleteTarget(null);
      fetchRecords();
    } catch (err) { console.error("Delete error:", err); }
  };

  // ── Field handler ─────────────────────────────────────────────────────
  const handle = (k, v) => {
    setFormData(p => ({ ...p, [k]: v }));
    setStepError("");
  };

  // ── Step navigation ───────────────────────────────────────────────────
  const tryGoToStep = (targetIdx) => {
    if (targetIdx < 0 || targetIdx >= STEPS.length) return;
    if (targetIdx < step) { setStep(targetIdx); setStepError(""); return; }
    for (let i = step; i < targetIdx; i++) {
      if (!stepComplete(i, formData)) {
        const missing = STEPS[i].fields.filter(f => !formData[f]);
        setStepError(`Complete Step ${i + 1} (${STEPS[i].label}) first. Missing: ${missing.map(f => f.replace(/_/g, " ")).join(", ")}.`);
        return;
      }
    }
    setStep(targetIdx);
    setStepError("");
  };

  // ── Save ──────────────────────────────────────────────────────────────
  const handleSave = async (status) => {
    if (status !== "draft") {
      const missing = ALL_REQUIRED_FIELDS.filter(f => !formData[f]);
      if (missing.length > 0) {
        setStepError(`Cannot publish — fill all required fields. Missing: ${missing.map(f => f.replace(/_/g, " ")).join(", ")}.`);
        return;
      }
    }

    setSaving(true);
    setSaveStatus(null);
    const now = getFormattedDate();
    const isDraft = status === "draft";

    const payload = {
      event_title:              formData.event_title              || (isDraft ? "Untitled Draft" : ""),
      event_type:               formData.event_type               || (isDraft ? " " : ""),
      custom_event_type:        formData.event_type === "Other"   ? (formData.custom_event_type || null) : null,
      event_mode:               formData.event_mode               || (isDraft ? " " : ""),
      event_date:               formData.event_date               || null,
      event_time:               formData.event_time               || null,
      venue:                    formData.venue                    || (isDraft ? " " : ""),
      description:              formData.description              || (isDraft ? " " : ""),
      target_audience:          formData.target_audience          || (isDraft ? " " : ""),
      custom_target_audience:   formData.target_audience?.includes("Other") ? (formData.custom_target_audience || null) : null,
      expected_participants:    formData.expected_participants     || 0,
      programs_featured:        formData.programs_featured        || (isDraft ? " " : ""),
      custom_programs:          formData.programs_featured?.includes("Other") ? (formData.custom_programs || null) : null,
      scholarships_discussed:   formData.scholarships_discussed   || (isDraft ? " " : ""),
      rep_available:            formData.rep_available            || (isDraft ? " " : ""),
      event_status:             status,
      modified_date:            now,
    };

    try {
      if (editIdRef.current) {
        const creds = Fetchupdatequery();
        await axios.post(creds[1], {
          schema_name: "edu_platform",
          table: "university_events",
          set_column: [{ ...payload, modified_by: uc.user_id }],
          where: [{ primary_id: editIdRef.current }],
        }, creds[0]);
      } else {
        const creds = CreateData();
        const res = await axios.post(creds[1], {
          database: "edu_platform",
          table: "university_events",
          formData: {
            ...payload,
            creator_user_id:   uc.user_id,
            creator_entity_id: uc.selectedentity || uc.entities_id,
            tenant_id:         uc.tenants_id,
            group_id:          uc.groups_id,
            created_by:        uc.user_id,
            created_date:      now,
            is_active:         1,
          },
        }, creds[0]);
        if (res?.data?.primary_id) {
          editIdRef.current = res.data.primary_id;
        }
      }
      setSaveStatus("saved");
      setFormData(p => ({ ...p, event_status: status }));
      setTimeout(() => setSaveStatus(null), 3500);
    } catch (err) {
      console.error("Save error:", err);
      setSaveStatus("error");
      setTimeout(() => setSaveStatus(null), 4000);
    } finally {
      setSaving(false);
    }
  };

  const goBackToList = () => { setView("list"); fetchRecords(); };

  const completion   = calcCompletion(formData);
  const maxReachable = lastUnlockedStep(formData);

  // ── Step content ──────────────────────────────────────────────────────
  const stepContent = [

    // ── Step 0: Event Info ──────────────────────────────────────────────
    <div key="s0">
      <Field label="Event Title" required>
        <Input
          value={formData.event_title}
          onChange={e => handle("event_title", e.target.value)}
          placeholder="e.g. Annual University Admission Open House 2025"
        />
      </Field>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
        {/* Event Type with Other input */}
        <Field label="Type of Event" required>
          <SelectInput
            value={formData.event_type}
            onChange={e => {
              handle("event_type", e.target.value);
              if (e.target.value !== "Other") handle("custom_event_type", "");
            }}
          >
            <option value="">Select type</option>
            {EVENT_TYPE_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
          </SelectInput>
          {formData.event_type === "Other" && (
            <Input
              value={formData.custom_event_type}
              onChange={e => handle("custom_event_type", e.target.value)}
              placeholder="Enter event type"
              style={{ marginTop: 10 }}
            />
          )}
        </Field>

        {/* Event Mode */}
        <Field label="Event Mode" required>
          <SelectInput
            value={formData.event_mode}
            onChange={e => handle("event_mode", e.target.value)}
          >
            <option value="">Select mode</option>
            {EVENT_MODE_OPTIONS.map(m => <option key={m} value={m}>{m}</option>)}
          </SelectInput>
        </Field>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
        <Field label="Event Date" required>
          <Input
            type="date"
            value={formData.event_date}
            onChange={e => handle("event_date", e.target.value)}
          />
        </Field>
        <Field label="Event Time" required>
          <Input
            type="time"
            value={formData.event_time}
            onChange={e => handle("event_time", e.target.value)}
          />
        </Field>
      </div>

      <Field
        label={formData.event_mode === "Virtual" ? "Platform Details" : formData.event_mode === "Hybrid" ? "Venue / Platform Details" : "Venue Details"}
        required
        hint={formData.event_mode === "Virtual" ? "e.g. Zoom, MS Teams — include the meeting link or platform name" : "Full venue name and address"}
      >
        <TextareaInput
          value={formData.venue}
          onChange={e => handle("venue", e.target.value)}
          placeholder={formData.event_mode === "Virtual" ? "e.g. Zoom Webinar — link to be shared upon registration" : "e.g. Main Auditorium, ABC University, 123 College Road"}
          style={{ minHeight: 80 }}
        />
      </Field>

      <Field label="Event Description" required hint="Provide a detailed overview of the event — goals, agenda, and what attendees can expect">
        <TextareaInput
          value={formData.description}
          onChange={e => handle("description", e.target.value)}
          placeholder="Describe the event in detail — purpose, structure, key activities…"
          style={{ minHeight: 120 }}
        />
      </Field>
    </div>,

    // ── Step 1: Audience ────────────────────────────────────────────────
    <div key="s1">
      <InfoBox>Define who this event is targeted at and how many participants you expect.</InfoBox>

      <Field label="Target Audience" required hint="Select all that apply — an 'Other' text field appears if needed">
        <PillGroup
          options={TARGET_AUDIENCE_OPTIONS}
          value={formData.target_audience}
          onChange={v => handle("target_audience", v)}
          otherValue={formData.custom_target_audience}
          onOtherChange={v => handle("custom_target_audience", v)}
        />
      </Field>

      <Field label="Expected Number of Participants" required hint="Estimated total headcount for this event">
        <Input
          type="number"
          min="0"
          value={formData.expected_participants}
          onChange={e => handle("expected_participants", e.target.value)}
          placeholder="e.g. 250"
          style={{ maxWidth: 280 }}
        />
      </Field>
    </div>,

    // ── Step 2: Academic Details ────────────────────────────────────────
    <div key="s2">
      <InfoBox>Specify the academic programs being showcased and what interactions will be available to attendees.</InfoBox>

      <Field label="Programs / Disciplines Featured" required hint="Select all programs that will be highlighted at this event">
        <PillGroup
          options={PROGRAMS_OPTIONS}
          value={formData.programs_featured}
          onChange={v => handle("programs_featured", v)}
          otherValue={formData.custom_programs}
          onOtherChange={v => handle("custom_programs", v)}
        />
      </Field>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 24, marginTop: 24 }}>
        <Field label="Will Scholarships or Financial Aid Opportunities Be Discussed?" required>
          <RadioGroup
            options={YES_NO_OPTIONS}
            value={formData.scholarships_discussed}
            onChange={v => handle("scholarships_discussed", v)}
          />
        </Field>

        <Field label="Will University Representatives Be Available for One-on-One Interaction?" required>
          <RadioGroup
            options={YES_NO_OPTIONS}
            value={formData.rep_available}
            onChange={v => handle("rep_available", v)}
          />
        </Field>
      </div>
    </div>,
  ];

  // ════════════════════════════════════════════════════════════════
  // RENDER: LIST VIEW
  // ════════════════════════════════════════════════════════════════
  if (view === "list") {
    return (
      <div style={{ minHeight: "100vh", background: T.bg }}>
        <h2 className="pt-1 pb-1 text-center" style={{ color: "#0a2352", fontSize: "18px" }}>
          UNIVERSITY EVENT MANAGEMENT
        </h2>

        <div style={{ margin: "0 auto", padding: "24px 32px 40px", boxSizing: "border-box" }}>
          <div style={{ display: "flex", gap: 16, marginBottom: 24, flexWrap: "wrap" }}>
            <StatCard label="Total Events"   value={totalCount}     icon={Calendar}     color={T.accent}  />
            <StatCard label="Published"      value={publishedCount} icon={FileText}      color={T.success} />
            <StatCard label="Drafts"         value={draftCount}     icon={Edit3}         color={T.warn}    />
            <StatCard label="Virtual Events" value={virtualCount}   icon={BarChart2}     color={T.purple}  />
          </div>

          <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 16, gap: 12 }}>
            <div style={{ position: "relative", width: 290 }}>
              <ClipboardList size={15} color={T.muted} style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)" }} />
              <input
                value={searchText}
                onChange={e => setSearchText(e.target.value)}
                placeholder="Search events…"
                style={{ ...inputBase, paddingLeft: 34, fontSize: 14 }}
              />
            </div>
            <Btn variant="primary" icon={Plus} onClick={openCreate}>Create Event</Btn>
          </div>

          {listLoading ? (
            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: 300 }}>
              <div style={{ width: 40, height: 40, border: `4px solid ${T.light}`, borderTop: `4px solid ${T.orange}`, borderRadius: "50%", animation: "spin 0.8s linear infinite" }} />
              <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
            </div>
          ) : (
            <Card style={{ overflow: "visible" }}>
              <div className="ag-theme-alpine" style={{ height: 520, width: "100%" }}>
                <AgGridReact
                  columnDefs={columnDefs}
                  rowData={records}
                  quickFilterText={searchText}
                  animateRows
                  pagination
                  paginationPageSize={12}
                  defaultColDef={{ sortable: true, filter: true, resizable: true }}
                  rowHeight={46}
                />
              </div>
            </Card>
          )}
        </div>

        {viewRecord   && <ViewModal record={viewRecord} onClose={() => setViewRecord(null)} />}
        {deleteTarget && (
          <ConfirmDialog
            message={`Are you sure you want to delete "${deleteTarget.event_title}"? This cannot be undone.`}
            onConfirm={confirmDelete}
            onCancel={() => setDeleteTarget(null)}
          />
        )}
      </div>
    );
  }

  // ════════════════════════════════════════════════════════════════
  // RENDER: FORM VIEW
  // ════════════════════════════════════════════════════════════════
  return (
    <div style={{ minHeight: "100vh", background: T.bg }}>
      <h2 className="pt-1 pb-1 text-center" style={{ color: "#0a2352", fontSize: "18px" }}>
        UNIVERSITY EVENT MANAGEMENT
      </h2>

      <div style={{ margin: "0 auto", padding: "24px 32px 48px", boxSizing: "border-box" }}>
        <div style={{ display: "grid", gridTemplateColumns: "minmax(0,1fr) 250px", gap: 24, alignItems: "start" }}>

          {/* ── Left: wizard ── */}
          <div>
            {/* Step tabs */}
            <div style={{ display: "flex", borderRadius: 12, overflow: "hidden", border: `1px solid ${T.border}`, marginBottom: 24, background: T.card }}>
              {STEPS.map((s, i) => {
                const isActive = step === i;
                const isDone   = stepComplete(i, formData);
                const isLocked = i > maxReachable + 1;
                const Icon     = s.icon;
                return (
                  <div key={i} onClick={() => tryGoToStep(i)}
                    style={{
                      flex: 1, padding: "14px 8px", minHeight: 82, textAlign: "center",
                      cursor: isLocked ? "not-allowed" : "pointer",
                      background: isActive
                        ? `linear-gradient(135deg, ${T.navy} 0%, ${T.navyMid} 100%)`
                        : isDone ? "rgba(22,163,74,.07)" : "transparent",
                      borderRight: i < STEPS.length - 1 ? `1px solid ${T.border}` : "none",
                      transition: "background .2s",
                      opacity: isLocked ? .45 : 1,
                    }}
                  >
                    <div style={{ display: "flex", justifyContent: "center", marginBottom: 5 }}>
                      {isDone
                        ? <Check size={16} color={isActive ? T.orange : T.success} />
                        : <Icon size={16} color={isActive ? T.orange : T.muted} />}
                    </div>
                    <div style={{ fontSize: 13, fontWeight: 800, color: isActive ? "#fff" : isDone ? T.success : T.muted, letterSpacing: .4 }}>{i + 1}</div>
                    <div style={{ fontSize: 13, fontWeight: 700, color: isActive ? "rgba(255,255,255,.92)" : isDone ? T.success : T.muted, marginTop: 2, lineHeight: 1.25 }}>{s.label}</div>
                    {isLocked && <div style={{ fontSize: 11, color: T.muted, marginTop: 2 }}>🔒</div>}
                  </div>
                );
              })}
            </div>

            {/* Step error banner */}
            {stepError && (
              <div style={{ display: "flex", alignItems: "flex-start", gap: 10, marginBottom: 16, padding: "12px 16px", background: "rgba(220,38,38,.07)", borderRadius: 8, border: `1px solid rgba(220,38,38,.25)` }}>
                <AlertTriangle size={16} color={T.danger} style={{ flexShrink: 0, marginTop: 2 }} />
                <span style={{ fontSize: 15, color: T.danger, lineHeight: 1.5 }}>{stepError}</span>
                <button onClick={() => setStepError("")} style={{ marginLeft: "auto", background: "none", border: "none", color: T.danger, cursor: "pointer", padding: 0 }}><X size={15} /></button>
              </div>
            )}

            {/* Step card */}
            <Card>
              <div style={{ padding: "14px 22px", borderBottom: `1px solid ${T.border}`, background: `linear-gradient(135deg, ${T.navy} 0%, ${T.navyMid} 100%)`, display: "flex", alignItems: "center", gap: 10 }}>
                <div style={{ background: T.orange, borderRadius: 7, padding: "5px 7px", display: "flex", alignItems: "center" }}>
                  {React.createElement(STEPS[step].icon, { size: 16, color: "#fff" })}
                </div>
                <div>
                  <div style={{ color: "#fff", fontWeight: 800, fontSize: 17 }}>
                    Step {step + 1} of {STEPS.length}: {STEPS[step].label}
                  </div>
                  <div style={{ color: "rgba(255,255,255,.65)", fontSize: 14, marginTop: 2 }}>
                    {stepComplete(step, formData)
                      ? "✓ This step is complete"
                      : `${STEPS[step].fields.filter(f => !formData[f]).length} required field(s) remaining`}
                  </div>
                </div>
              </div>

              <div style={{ padding: 28 }}>{stepContent[step]}</div>

              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "14px 28px", borderTop: `1px solid ${T.border}`, background: T.bg }}>
                <Btn variant="outline" icon={ChevronLeft} onClick={() => tryGoToStep(step - 1)} disabled={step === 0}>Previous</Btn>
                <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
                  {saveStatus === "saved" && (
                    <span style={{ display: "flex", alignItems: "center", gap: 5, color: T.success, fontWeight: 700, fontSize: 15 }}>
                      <CheckCircle size={15} /> Saved
                    </span>
                  )}
                  {saveStatus === "error" && (
                    <span style={{ display: "flex", alignItems: "center", gap: 5, color: T.danger, fontWeight: 700, fontSize: 15 }}>
                      <AlertCircle size={15} /> Save failed
                    </span>
                  )}
                  <Btn variant="outline" icon={Save} onClick={() => handleSave("draft")} disabled={saving} size="sm">
                    Save Draft
                  </Btn>
                  {step < STEPS.length - 1 ? (
                    <Btn variant="primary" onClick={() => tryGoToStep(step + 1)}>
                      Next <ChevronRight size={15} />
                    </Btn>
                  ) : (
                    <Btn variant="success" icon={Zap} onClick={() => handleSave("published")} disabled={saving}>
                      {saving ? "Publishing…" : "Publish Event"}
                    </Btn>
                  )}
                </div>
              </div>
            </Card>
          </div>

          {/* ── Right sidebar ── */}
          <div style={{ position: "sticky", top: 76 }}>
            <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 10 }}>
              <Btn variant="outline" icon={List} onClick={goBackToList}
                style={{ background: `linear-gradient(135deg, ${T.navyDark} 0%, ${T.navy} 100%)`, color: "#fff", borderColor: "rgba(255,255,255,.4)" }}>
                Back to List
              </Btn>
            </div>

            {/* Progress */}
            <Card style={{ padding: 18, marginBottom: 14 }}>
              <div style={{ fontWeight: 800, fontSize: 14, color: T.navy, textTransform: "uppercase", letterSpacing: .5, marginBottom: 10 }}>Overall Progress</div>
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
                <div style={{ flex: 1, background: T.light, borderRadius: 99, height: 9, overflow: "hidden" }}>
                  <div style={{ width: `${completion}%`, height: "100%", borderRadius: 99, transition: "width .5s", background: completion === 100 ? T.success : T.orange }} />
                </div>
                <span style={{ fontSize: 15, fontWeight: 800, color: completion === 100 ? T.success : T.orange, minWidth: 36, textAlign: "right" }}>{completion}%</span>
              </div>
              <div style={{ fontSize: 14, color: T.muted }}>
                {ALL_REQUIRED_FIELDS.filter(f => formData[f]).length} of {ALL_REQUIRED_FIELDS.length} required fields filled
              </div>
            </Card>

            {/* Steps nav */}
            <Card style={{ padding: 18, marginBottom: 14 }}>
              <div style={{ fontWeight: 800, fontSize: 14, color: T.navy, textTransform: "uppercase", letterSpacing: .5, marginBottom: 10 }}>Steps</div>
              {STEPS.map((s, i) => {
                const done   = stepComplete(i, formData);
                const locked = i > maxReachable + 1;
                const Icon   = s.icon;
                return (
                  <div key={i} onClick={() => !locked && tryGoToStep(i)}
                    style={{ display: "flex", alignItems: "center", gap: 8, padding: "9px 10px", borderRadius: 7, marginBottom: 4, cursor: locked ? "not-allowed" : "pointer", background: step === i ? "rgba(10,35,82,.06)" : "transparent", border: step === i ? `1px solid ${T.border}` : "1px solid transparent", opacity: locked ? .5 : 1 }}
                  >
                    <div style={{ width: 26, height: 26, borderRadius: "50%", flexShrink: 0, background: done ? T.success : step === i ? T.orange : T.light, display: "flex", alignItems: "center", justifyContent: "center" }}>
                      {done ? <Check size={13} color="#fff" /> : <Icon size={13} color={step === i ? "#fff" : T.muted} />}
                    </div>
                    <span style={{ fontSize: 15, fontWeight: step === i ? 700 : 500, color: step === i ? T.navy : T.muted, flex: 1 }}>{s.label}</span>
                    {locked && <span style={{ fontSize: 12 }}>🔒</span>}
                    {done   && <span style={{ fontSize: 13, color: T.success, fontWeight: 700 }}>✓</span>}
                  </div>
                );
              })}
            </Card>

            {/* Status */}
            <Card style={{ padding: 16 }}>
              <div style={{ fontSize: 14, color: T.muted, fontWeight: 700, textTransform: "uppercase", letterSpacing: .5, marginBottom: 8 }}>Event Status</div>
              {statusBadge(formData.event_status || "draft")}
              <div style={{ marginTop: 10, fontSize: 15, color: T.muted, lineHeight: 1.6 }}>
                {formData.event_status === "draft"
                  ? "Save Draft keeps your progress. Publish makes the event live."
                  : formData.event_status === "published"
                  ? "This event is published and visible."
                  : STATUS_META[formData.event_status]?.label || ""}
              </div>
            </Card>
          </div>

        </div>
      </div>
    </div>
  );
}

// Needed so inputBase is accessible inside the list search input


export default UniversityEventManagement;